import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { IPathParams, IRouteConfig } from "@/app/routing/types";
import { getAbilitiesFromLocalStorage } from "@/shared/api/utils/get-token";
import { BotURLQueryParams, PartyQueryParams } from "@/shared/routing";
import { checkAbility, checkParty } from "@/shared/utils/check-abilities";

import { AntMenuItemType } from "../types";
import { MenuIcon } from "../ui/navigation-menu/menu-icon";
import { MenuLabel } from "../ui/navigation-menu/menu-label";
import { checkIsSubMenu, getMenuItemKey } from "../utils";

import { useGetLastSegment } from "./use-get-party-current-tab";
import { useLoadIndication } from "./use-load-indication";

const checkMenuItemHide = ({ menuItem, hide }: IRouteConfig) =>
	!!menuItem && (hide === undefined || !hide);

const filterRoutesByAbilitiesAndHide = (
	routes: IRouteConfig[],
	partyUUID?: string
): IRouteConfig[] => {
	const abilities = getAbilitiesFromLocalStorage();

	return routes.filter((route) => {
		if (checkIsSubMenu(route))
			route.children = filterRoutesByAbilitiesAndHide(route.children as IRouteConfig[], partyUUID);

		const isHide = checkMenuItemHide(route);

		if (!isHide) return false;

		if (!partyUUID) return route.ability ? checkAbility(abilities, route.ability) : true;

		return route.ability ? checkParty(abilities, route.ability, partyUUID) : true;
	});
};

interface BuildMenuItemsParams {
	routeItems: IRouteConfig[];
	pathParams?: IPathParams;
	parentPath?: string;
}

const buildMenuItems = ({
	routeItems,
	pathParams,
	parentPath,
}: BuildMenuItemsParams): AntMenuItemType[] =>
	routeItems.map((route): AntMenuItemType => {
		const { menuItem, path, children } = route;

		if (!menuItem) return null;

		const preparedPath = parentPath && path ? `${parentPath}/${path}` : path;

		let subMenuProps = {};

		if (checkIsSubMenu(route)) {
			const subMenuChildren = buildMenuItems({
				routeItems: children as IRouteConfig[],
				pathParams,
				parentPath: preparedPath,
			});

			if (subMenuChildren.length) subMenuProps = { type: "submenu", children: subMenuChildren };
		}

		if (!Object.keys(subMenuProps).length && !preparedPath) return null;

		return {
			key: getMenuItemKey(route),
			label: <MenuLabel route={preparedPath} pathParams={pathParams} menuItem={menuItem} />,
			icon: <MenuIcon menuItem={menuItem}>{menuItem.icon}</MenuIcon>,
			type: "item",
			...subMenuProps,
		};
	});

export const useGetNavigationList = (
	items: IRouteConfig[],
	pathParams?: IPathParams
): AntMenuItemType[] => {
	const currentTab = useGetLastSegment();

	const params = useParams<keyof PartyQueryParams | keyof BotURLQueryParams>();

	const permittedRouteItems = useMemo(
		() => filterRoutesByAbilitiesAndHide(items, params.partyUUID),
		[items, params.partyUUID]
	);

	useLoadIndication({
		routeItems: permittedRouteItems,
		partyUUID: params.partyUUID,
		uuid: params.uuid,
		currentTab,
	});

	return buildMenuItems({ routeItems: permittedRouteItems, pathParams });
};
